import React, { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { Redirect } from 'react-router'
import { Route, Switch, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PermContactCalendarIcon from '@material-ui/icons/PermContactCalendar'

import PageHeader from 'App/components/PageHeader'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'
import OutlinedButton from 'civic-champs-shared/core/OutlinedButton'
import CopyButton from 'civic-champs-shared/core/copy-button'
import useSuccessNotification from 'civic-champs-shared/api/hooks/useSuccessNotification'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'
import { useShowEventAddEditPrompt } from 'Event/hooks/useShowEventAddEditPrompt'
import { encodeOccurrenceFromEvent } from 'Event/helpers/encodeOccurrence'
import { MonthView } from './MonthView'
import { WeekView } from './WeekView'
import { DayView } from './DayView'
import { currentViewTypeSelector } from '../../redux/reducers/ViewEvents'
import { viewTypes } from './viewTypes'

const useStyles = makeStyles(theme => ({
  calendarScene: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 48px)',

    '&$dayView': {
      height: 'auto !important',
    },
  },
  dayView: {},
  actionsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  actionsLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  volunteerWebCalendarButton: {
    color: '#3776CF',
    borderColor: '#3776CF',
    '&:hover': {
      borderColor: '#3776CF',
      backgroundColor: 'rgba(55, 118, 207, 0.04)',
    },
    '& .MuiButton-startIcon': {
      color: '#3776CF',
    },
  },
}))

const useVolunteerWebCalendarUrl = () => {
  const currentOrg = useCurrentOrg()

  return useMemo(
    () => `${process.env.REACT_APP_EVENTS_SITE_ENDPOINT}/organizations/${currentOrg.id}/calendar/month`,
    [currentOrg.id],
  )
}

const viewTypeToComponentMap = {
  [viewTypes.DAY]: DayView,
  [viewTypes.WEEK]: WeekView,
  [viewTypes.MONTH]: MonthView,
}

const useStartCreateEvent = history => {
  const currentOrg = useCurrentOrg()
  const newEventEditUIEnabled = useFeatureEnabled('NewEditEventUICalendarEvent')

  const onNewEventCreated = useCallback(
    event => {
      let url
      if (event.isOffer) {
        url = `/offers/${encodeOccurrenceFromEvent(event)}`
      } else {
        url = event.schedulable
          ? `/events/${encodeOccurrenceFromEvent(event)}/${currentOrg.id}`
          : `/opportunities/${encodeOccurrenceFromEvent(event)}`
      }
      history.push(url)
    },
    [currentOrg.id, history],
  )

  const showCreatePrompt = useShowEventAddEditPrompt(onNewEventCreated)

  return () => {
    if (newEventEditUIEnabled) {
      showCreatePrompt()
    } else {
      history.push('/event/new-event/basic')
    }
  }
}

export default function Events(props) {
  const { history } = props
  const params = useParams()
  const persistedViewType = useSelector(currentViewTypeSelector)
  const viewType = params.viewType || persistedViewType
  const classes = useStyles()
  const volunteerCalendarUrl = useVolunteerWebCalendarUrl()
  const onCopySuccess = useSuccessNotification(true)

  const handleCreate = useStartCreateEvent(history)

  const handleOpenVolunteerCalendar = useCallback(() => {
    window.open(volunteerCalendarUrl, '_blank', 'noopener,noreferrer')
  }, [volunteerCalendarUrl])

  const handleCopyVolunteerCalendarLink = useCallback(async () => {
    await navigator.clipboard.writeText(volunteerCalendarUrl)
    onCopySuccess('Link copied')
  }, [onCopySuccess, volunteerCalendarUrl])

  return (
    <PageHeader title="Calendar" subTitle="View, create, and manage past and upcoming events" isolateContentTheme>
      <div className={classes.actionsBar}>
        <div className={classes.actionsLeft}>
          <OutlinedButton
            className={classes.volunteerWebCalendarButton}
            startIcon={<PermContactCalendarIcon />}
            onClick={handleOpenVolunteerCalendar}
          >
            View Volunteer Web Calendar
          </OutlinedButton>
          <CopyButton color="primary" onClick={handleCopyVolunteerCalendarLink} />
        </div>
        <ContainedButton startIcon={<AddIcon />} onClick={handleCreate}>
          Add Event
        </ContainedButton>
      </div>

      <article
        className={cn(classes.calendarScene, {
          [classes.dayView]: viewType === viewTypes.DAY,
        })}
      >
        <Switch>
          <Route
            path={'/events/:viewType'}
            render={() => {
              const Component = viewTypeToComponentMap[viewType]

              return <Component />
            }}
          />
          <Redirect from="/events" to={`/events/${viewType}`} />
        </Switch>
      </article>
    </PageHeader>
  )
}
